import React from 'react';
import styled from 'styled-components';

import { Button } from 'src/presentation/components/agent_desktop_components';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import { formatPhone } from 'src/actions/shared/phonesHelper';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import LinkOutStrokeV2 from '@ad-scripts/presentation/components/common/icons/stroke/link-out-v2-stroke';
import { NumberPreference } from 'src/presentation/components/pages/channels/shared/helpers/stageConfiguration';
import { PhoneNumberType } from 'src/actions/availableNumbers/availableNumbersResource';
import { StepWizardActionContainer } from './shared/elements';
import { StepWizardFieldWithBorder } from 'src/presentation/components/pages/email/components/StepWizardLayout';

const PhoneSmsVerification = ({ nextStep, stageConfig }: { nextStep?; stageConfig: any }) => {
  const {
    selectedPhone: { areaCode, phoneNumber, phoneNumberType },
  } = stageConfig;

  const formattedPhoneNumber = formatPhone(phoneNumber);

  const isExisting = stageConfig.numberPreference === NumberPreference.exists;

  return (
    <>
      <StepWizardFieldWithBorder>
        {phoneNumberType === PhoneNumberType.LOCAL && (
          <>
            <H3 data-aid="wizard-title">A2P 10DLC Registration</H3>

            {!isExisting && (
              <StyledDescription>
                Open the link below to initiate the required A2P 10DLC registration for newly purchased US and CA phone
                numbers. Skip this step and click &quot;Next&quot; if the phone number will not be used for SMS.
              </StyledDescription>
            )}

            {isExisting && (
              <StyledDescription>
                By clicking &quot;Next,&quot; you&apos;re confirming that you&apos;ve received the A2P 10DLC
                Registration approval from Gladly Support,
                <br /> which ensures the deliverability of SMS sent through 10DLC numbers, or you will not use this
                phone number for SMS. Otherwise, click the link below
                <br /> to begin the A2P 10DLC registration process and return to activate the phone number again once
                you receive the approval.
              </StyledDescription>
            )}

            <StyledExternalLink
              className="external-link"
              data-aid="learnAbout"
              icon={<StyledLinkOutStrokeV2 />}
              label={'Twilio Business Registration "A2P 10DLC"'}
              showIcon={true}
              to="https://www.surveymonkey.com/r/Twilio_A2P_10DLC_Registration"
            />
            <StyledDescription>
              A2P 10DLC numbers ({formattedPhoneNumber} where {areaCode} is a local area code) must be registered before
              use for messaging.
              <br /> A2P 10DLC numbers are governed by the FCC and CTIA and require businesses to follow guidelines and
              regulations such
              <br /> as the TCPA in the US.
            </StyledDescription>
          </>
        )}
        {phoneNumberType === PhoneNumberType.TOLL_FREE && (
          <>
            <H3 data-aid="wizard-title">Toll-Free Verification</H3>

            {!isExisting && (
              <StyledDescription>
                Open the link below to initiate the required Toll-Free verification process for newly purchased US and
                CA phone numbers. Skip this step and click &quot;Next&quot; if the phone number will not be used for
                SMS.
              </StyledDescription>
            )}
            {isExisting && (
              <StyledDescription>
                By clicking &quot;Next,&quot; you&apos;re confirming that you&apos;ve received the Toll-Free
                Verification approval from Gladly Support, which
                <br /> ensures the deliverability of messages sent through Toll-Free numbers, or you will not use this
                phone number for SMS. Otherwise, click the link below to begin the Toll-Free number verification process
                and return to activate the phone number again once you receive the approval.
              </StyledDescription>
            )}

            <StyledExternalLink
              className="external-link"
              data-aid="learnAbout"
              icon={<StyledLinkOutStrokeV2 />}
              label="Toll-Free Number Messaging Verification"
              showIcon={true}
              to="https://www.surveymonkey.com/r/Twilio-Toll-Free-Registration"
            />
            <StyledDescription>
              Toll-Free numbers (e.g., +1 8NN XXX-XXXX where NN is two repeated digits like +1 855) in the US and CA
              must be verified
              <br /> to send messages and ensure delivery by reducing network-level filtering of messages. Unverified
              Toll-Free phone
              <br /> numbers have lower volume limits and a higher chance of being tagged as spam by networks.
            </StyledDescription>
          </>
        )}
      </StepWizardFieldWithBorder>
      <StepWizardActionContainer>
        <Button data-aid="editor-save" type={Button.Types.PRIMARY} onClick={nextStep}>
          Next
        </Button>
      </StepWizardActionContainer>
    </>
  );
};

const StyledLinkOutStrokeV2 = styled(LinkOutStrokeV2)`
  fill: ${p => p.theme.colors.green400};
`;

const StyledExternalLink = styled(ExternalLink)`
  font-weight: 700;

  svg {
    position: relative;
    top: 3px;
  }
`;

const StyledDescription = styled.p`
  line-height: 20px;
  margin-bottom: 20px;
`;

export default PhoneSmsVerification;
